import { create } from "zustand";

// Top-level view selector. Drives which page App renders after login.
//
//   pipeline → existing kanban Board (the default — most-used view)
//   earnings → Cleaner Earnings dashboard
//   growth   → Business Growth (stub)
//   marketing → Marketing (stub)
//   coverage → Tech Coverage (long-absence detection lives here)
//   areas    → Area Analysis (wealth × revenue × competition)
//   recruitment → Technician recruitment board (Andzelika's outreach pipeline)
//   db       → DB board (Darren's private subscription tracker — gated to
//               darren@carpetcleaning.co.uk only at the API layer)
//
// Persisted to sessionStorage so a hard refresh on /earnings stays on the
// earnings page. Navigation is in-app state only (no react-router yet) so
// URL rewrites aren't needed.

export const VIEWS = ["pipeline", "schedule", "earnings", "growth", "marketing", "coverage", "areas", "recruitment", "waitinglist", "team", "db"] as const;
export type View = (typeof VIEWS)[number];

const STORAGE_KEY = "breeze.view";

function loadInitialView(): View {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw && (VIEWS as readonly string[]).includes(raw)) return raw as View;
  } catch {
    // sessionStorage can be unavailable in private mode; fall through.
  }
  return "pipeline";
}

type ViewState = {
  view: View;
  setView: (view: View) => void;
};

export const useViewStore = create<ViewState>((set) => ({
  view: loadInitialView(),
  setView: (view) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, view);
    } catch {
      // ignore — the in-memory state is still updated
    }
    set({ view });
  },
}));

export const useView = (): View => useViewStore((s) => s.view);
export const setView = (view: View): void => useViewStore.getState().setView(view);

export const VIEW_LABELS: Record<View, string> = {
  pipeline: "Pipeline",
  schedule: "Schedule",
  earnings: "Cleaner Earnings",
  growth: "Business Growth",
  marketing: "Marketing",
  coverage: "Tech Coverage",
  areas: "Area Analysis",
  recruitment: "Recruitment",
  waitinglist: "Waiting List",
  team: "Team",
  db: "DB",
};
